import { isPrimitiveTypeLexeme } from "../spec/tokens.js";

export class Parser {
    constructor(tokens) {
        this.t = tokens;
        this.i = 0;
        this.errors = [];
    }
    atEnd() { return this.i >= this.t.length; }
    peek() { return this.atEnd() ? null : this.t[this.i]; }
    next() { return this.atEnd() ? null : this.t[this.i++]; }
    match(...types) {
        const p = this.peek(); if (p && types.includes(p.type)) { this.i++; return p; }
        return null;
    }
    expect(type, msg) {
        const p = this.match(type);
        if (!p) {
            const here = this.peek() ?? this.t[Math.max(0, this.t.length - 1)];
            this.errors.push({ message: msg, pos: here ? here.from : { index: 0, line: 1, col: 1 } });
        }
        return p;
    }

    parse() {
        const node = this.ruleNode();
        if (!node) return null;
        if (!this.atEnd()) {
            const extra = this.peek();
            if (extra) this.errors.push({ message: `Trailing text after rule: '${extra.text}'`, pos: extra.from });
        }
        return node;
    }

    // <rule-node> ::= "if" "(" <condition> ")" "{" <block> "}" ["else" "{" <block> "}"]
    ruleNode() {
        this.expect("KW_IF", "Expected 'if'");
        this.expect("LPAREN", "Expected '(' after 'if'");
        const cond = this.condition();
        this.expect("RPAREN", "Expected ')' after condition");
        this.expect("LBRACE", "Expected '{' for if-block");
        const thenBlock = this.block();
        this.expect("RBRACE", "Expected '}' after if-block");

        let elseBlock = null;
        if (this.match("KW_ELSE")) {
            this.expect("LBRACE", "Expected '{' for else-block");
            elseBlock = this.block();
            this.expect("RBRACE", "Expected '}' after else-block");
        }
        return { kind: "RuleNode", condition: cond ?? { kind: "BlockEmpty" }, thenBlock, elseBlock };
    }

    block() {
        const p = this.peek();
        if (!p || p.type === "RBRACE") return { kind: "BlockEmpty" };
        if (p.type === "KW_IF") return this.ruleNode();
        const start = this.i;
        while (!this.atEnd() && this.peek().type !== "RBRACE") this.i++;
        const text = this.t.slice(start, this.i).map(t => t.text).join("");
        return text.trim().length ? { kind: "BlockValue", text } : { kind: "BlockEmpty" };
    }

    condition() { return this.or(); }

    or() {
        const items = [];
        const first = this.and();
        if (!first) return null;
        items.push(first);
        while (this.peek() && (this.peek().opLexeme === "|" || this.peek().text === "|")) {
            this.next();
            const rhs = this.and();
            if (rhs) items.push(rhs);
        }
        return items.length === 1 ? first : { kind: "ConditionOr", items };
    }

    and() {
        const items = [];
        const first = this.cmpExpr();
        if (!first) return null;
        items.push(first);
        while (this.peek() && (this.peek().opLexeme === "&" || this.peek().text === "&")) {
            this.next();
            const rhs = this.cmpExpr();
            if (rhs) items.push(rhs);
        }
        return items.length === 1 ? first : { kind: "ConditionAnd", items };
    }

    cmpExpr() {
        if (this.match("LPAREN")) {
            const inner = this.condition();
            this.expect("RPAREN", "Expected ')' after sub-expression");
            return inner ? { kind: "Paren", expr: inner } : null;
        }
        const left = this.operand();
        if (!left) return null;

        const op = this.match("OP");
        if (!op) {
            const prev = this.t[this.i - 1] ?? this.peek();
            this.errors.push({ message: "Expected comparison or membership operator", pos: prev ? (prev.to ?? prev.from) : { index: 0, line: 1, col: 1 } });
            return left;
        }

        if (op.opLexeme === "<@" || op.opLexeme === "!<@") {
            const right = this.listOperand();
            if (!right) return null;
            return { kind: "Membership", left, op: op.opLexeme, right };
        }

        const right = this.operand();
        if (!right) return null;
        return { kind: "Compare", left, op: op.opLexeme, right };
    }

    operand() {
        const p = this.peek();
        if (!p) return null;

        // $var%t[?
        if (this.match("DOLLAR")) {
            const name = this.identifierOrBackticked();
            this.expect("PERCENT", "Expected '%' after variable name");
            const typeTok = this.match("ALPHA");
            if (!typeTok || !isPrimitiveTypeLexeme(typeTok.text)) {
                this.errors.push({ message: "Expected type after '%': d|s|b", pos: (typeTok ?? this.peek() ?? p).from });
                return null;
            }
            const isList = !!this.match("LIST_BRACKET");
            return { kind: "TypedVar", name, type: typeTok.text, isList };
        }

        // literal%t[? (e.g. 123%s, `a,b`%s[ )
        if (this.peek()?.type === "BACKTICK" || this.peek()?.type === "ALPHA" || this.peek()?.type === "NUMBER") {
            const body = this.consumeLiteralBody();
            this.expect("PERCENT", "Expected '%' after literal");
            const typeTok = this.match("ALPHA");
            if (!typeTok || !isPrimitiveTypeLexeme(typeTok.text)) {
                this.errors.push({ message: "Expected type after '%': d|s|b", pos: (typeTok ?? this.peek() ?? p).from });
                return null;
            }
            const isList = !!this.match("LIST_BRACKET");
            return { kind: "TypedLiteral", body, type: typeTok.text, isList };
        }

        this.errors.push({ message: "Expected operand (typed variable or typed literal)", pos: p.from });
        this.next();
        return null;
    }

    consumeLiteralBody() {
        let parts = [];
        parts.push(this.consumeLiteralSegment());
        while (this.peek() && this.peek().type === "COMMA") {
            // const commaTok =
            this.next();
            const seg = this.consumeLiteralSegment();
            if (seg === "") {
                parts.push("");
            } else {
                parts.push(seg);
            }
        }
        return parts.join(",");
    }

    consumeLiteralSegment() {
        const t = this.peek();
        if (!t) return "";
        if (this.match("BACKTICK")) {
            return this.consumeBackticked();
        }
        if (this.match("ALPHA") || this.match("NUMBER")) {
            return this.t[this.i - 1].text;
        }
        return "";
    }

    listOperand() {
        const pos0 = this.peek() ? this.peek().from : { index: 0, line: 1, col: 1 };
        const opnd = this.operand();
        if (!opnd) return null;
        if ((opnd.kind === "TypedVar" || opnd.kind === "TypedLiteral") && opnd.type === "s" && opnd.isList) {
            return opnd;
        }
        this.errors.push({ message: "Right operand for <@/!<@ must be a string list '%s['", pos: pos0 });
        return opnd;
    }

    identifierOrBackticked() {
        const t = this.peek();
        if (!t) return "";
        if (this.match("ALPHA")) return this.t[this.i - 1].text;
        if (this.match("BACKTICK")) return this.consumeBackticked();
        this.errors.push({ message: "Expected identifier or backticked name", pos: t.from });
        this.next();
        return "";
    }

    consumeBackticked() {
        let buf = "";
        while (!this.atEnd()) {
            const tok = this.next();
            if (tok.type === "BACKTICK") return buf;
            buf += tok.text;
        }
        const last = this.t[Math.max(0, this.t.length - 1)];
        this.errors.push({ message: "Unclosed backticked string", pos: last ? last.to : { index: 0, line: 1, col: 1 } });
        return buf;
    }
}
